import { Link, useRouterState } from '@tanstack/react-router';
import type { Machine, MachineStatus } from '@bridgewerk/shared';
import { useMachines } from '../../contexts/MachineContext.js';
import { StatusDot } from './StatusDot.js';

const STATUS_TAG: Record<MachineStatus, string> = {
  running: 'RUN',
  idle: 'IDL',
  alarm: 'ALM',
  offline: 'OFF',
};

const STATUS_TAG_COLOR: Record<MachineStatus, string> = {
  running: 'text-green',
  idle: 'text-yellow',
  alarm: 'text-red',
  offline: 'text-muted',
};

const NAV_ITEMS = [
  { label: 'Overview', to: '/dashboard' },
  { label: 'Tooling', to: '/tooling' },
  { label: 'Schedule', to: '/schedule' },
  { label: 'Settings', to: '/settings' },
] as const;

export function Sidebar(): JSX.Element {
  const { machines, isLoading, isError } = useMachines();

  return (
    <aside className="bg-surface border-r border-border h-full overflow-y-auto">
      <SectionLabel>Navigation</SectionLabel>
      <nav className="px-2 pb-4">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.to} to={item.to} label={item.label} />
        ))}
      </nav>

      <SectionLabel>Machines</SectionLabel>
      <div className="px-2 pb-6 space-y-1">
        {isLoading && <SidebarSkeleton />}
        {isError && (
          <p className="px-2 py-2 text-xs text-text-dim">
            Couldn&apos;t load machines.
          </p>
        )}
        {!isLoading && !isError && machines.length === 0 && (
          <p className="px-2 py-2 text-xs text-text-dim">No machines yet.</p>
        )}
        {machines.map((m) => <MachineRow key={m.machine_id} machine={m} />)}
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="px-4 pt-5 pb-2 font-mono uppercase"
      style={{
        fontSize: 8,
        letterSpacing: '0.3em',
        color: '#1E3448',
      }}
    >
      {children}
    </div>
  );
}

function NavItem({ to, label }: { readonly to: string; readonly label: string }): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={[
        'flex items-center px-3 py-2 text-sm rounded-md transition-colors',
        'border-l-2',
        isActive
          ? 'border-l-accent bg-card text-text'
          : 'border-l-transparent text-text-dim hover:text-text hover:bg-card/40',
      ].join(' ')}
    >
      {label}
    </Link>
  );
}

function MachineRow({ machine }: { readonly machine: Machine }): JSX.Element {
  return (
    <Link
      to="/machines/$machineId"
      params={{ machineId: machine.machine_id }}
      search={{ tab: 'overview' }}
      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-text-dim hover:text-text hover:bg-card/40 transition-colors"
    >
      <StatusDot status={machine.status} size={8} />
      <span className="flex-1 text-xs font-mono truncate">{machine.machine_id}</span>
      <span
        className={`text-[10px] font-mono uppercase tracking-wider ${STATUS_TAG_COLOR[machine.status]}`}
      >
        {STATUS_TAG[machine.status]}
      </span>
    </Link>
  );
}

function SidebarSkeleton(): JSX.Element {
  return (
    <div className="space-y-1 px-2">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-7 rounded-md bg-card/50 animate-pulse"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}
